// src/App.js
import React, { useState, useEffect } from 'react';
import './App.css';

import LoginScreen from './pages/LoginScreen';
import SetupScreen from './pages/SetupScreen';
import Dashboard from './pages/Dashboard';
import { NotificationProvider } from './context/NotificationContext';
import { api } from './services/api';

const StaticFooter = () => (
  <footer
    style={{
      width: '100%',
      padding: '10px 0',
      backgroundColor: '#2c3e50',
      borderTop: '1px solid #eee',
      textAlign: 'center',
      fontSize: '0.8rem',
      color: '#d2d2d2ff',
      position: 'fixed',
      bottom: 0,
      left: 0,
      zIndex: 1000,
    }}
  >
    &copy; 2026 Echion Health. All rights reserved.
    <br />
  </footer>
);

function App() {
  const [user, setUser] = useState(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isSetupComplete, setIsSetupComplete] = useState(
    () => localStorage.getItem('isSetupComplete') === 'true'
  );
  const [vaultFiles, setVaultFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('activeTab') || 'report'
  );

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setIsSetupComplete(false);
    setVaultFiles([]);
    setActiveTab('report');
  };

  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('activeTab', activeTab);
    }
  }, [activeTab]);

  // ─────────────────────────────────────────────────────────────
  // Session check on boot
  // 1. Verify token exists
  // 2. Hydrate user from localStorage (fast path)
  // 3. Verify with backend in the background (catches expired tokens)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('authToken');
      const savedUserStr = localStorage.getItem('user');

      console.log('[App] Boot. Token present:', !!token);

      if (!token) {
        setUser(null);
        setIsAuthChecking(false);
        return;
      }

      // Fast path: trust the stored user object if present
      let currentUser = null;
      if (savedUserStr) {
        try {
          currentUser = JSON.parse(savedUserStr);
        } catch (err) {
          console.error('[App] Failed to parse stored user:', err);
          localStorage.removeItem('user');
        }
      }

      if (currentUser) {
        setUser(currentUser);
      }

      // Background verification: only if we have a token
      try {
        const res = await api.getMe();
        const freshUser = res?.data || res;
        if (freshUser && freshUser.email) {
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
          console.log(
            '[App] Session verified:',
            freshUser.email,
            '/',
            freshUser.role
          );
        }
      } catch (error) {
        console.warn('[App] Session verification failed:', error.message);
        // Only log out if the server explicitly says the token is bad
        if (
          error.message.includes('401') ||
          error.message.includes('session has expired')
        ) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          setUser(null);
        }
        // For 403 / network errors, keep the user logged in offline
      } finally {
        setIsAuthChecking(false);
      }
    };

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetupComplete = () => {
    setIsSetupComplete(true);
    localStorage.setItem('isSetupComplete', 'true');
  };

  const handleAddFiles = () => {
    setIsSetupComplete(false);
    localStorage.setItem('isSetupComplete', 'false');
  };

  if (isAuthChecking) return null;
  if (!user) return <LoginScreen />;

  if (!isSetupComplete) {
    return (
      <>
        <SetupScreen
          onComplete={handleSetupComplete}
          userName={
            user.firstName || user.displayName || user.email || 'User'
          }
          setVaultFiles={setVaultFiles}
        />
        <StaticFooter />
      </>
    );
  }

  return (
    <NotificationProvider userId={user.id || user.uid}>
      <div className="App">
        <Dashboard
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          userProfile={user}
          onLogout={handleLogout}
          vaultFiles={vaultFiles}
          onAddFiles={handleAddFiles}
        />
      </div>
    </NotificationProvider>
  );
}

export default App;